import { useEffect, useState } from "react";
import { api } from "@cliqsuite/db/powersync";
// account.ts imports this file too; both only call each other later, which a cycle allows.
import { accountToken } from "./account";

// The guest's "account" is what this phone holds: a profile (name, phone) and one member ticket per
// business it joined (written by session.tsx's signInAs). Nothing here is a server-side guest identity.
// ponytail: a new phone starts empty; joining a store again with the same number brings its card back.

const PROFILE_KEY = "cliqsuite_guest_profile";
const MEMBER_PREFIX = "cliqsuite_guest_member:";

export interface GuestProfile {
  name: string;
  phone: string;
}

export function readProfile(): GuestProfile | null {
  try {
    const p = JSON.parse(localStorage.getItem(PROFILE_KEY) ?? "null") as GuestProfile | null;
    return p?.name && p.phone ? p : null;
  } catch {
    return null;
  }
}

export function saveProfile(p: GuestProfile) {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
  } catch {
    // private mode: the profile just isn't remembered
  }
}

const SEEN_KEY = "cliqsuite_guest_receipts_seen";

/** When the Receipts page was last opened; a first visit counts the last day as new. */
export function receiptsSeenAt(): number {
  try {
    return Number(localStorage.getItem(SEEN_KEY)) || Date.now() - 86_400_000;
  } catch {
    return Date.now();
  }
}

export function markReceiptsSeen() {
  try {
    localStorage.setItem(SEEN_KEY, String(Date.now()));
  } catch {
    // nothing to remember
  }
}

/** Removes the profile and every member card from this phone. The stores keep their member records. */
export function forgetPhone() {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key === PROFILE_KEY || key === SEEN_KEY || key === "cliqsuite_guest_account" || key.startsWith(MEMBER_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // nothing stored
  }
}

function tickets(): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(MEMBER_PREFIX)) continue;
      const t = (JSON.parse(localStorage.getItem(key) ?? "null") as { ticket?: string } | null)?.ticket;
      if (t) out.push(t);
    }
  } catch {
    // no storage, no cards
  }
  return out;
}

export interface WalletCard {
  member_id: string;
  business_id: string;
  business_name: string;
  member_number: string;
  name: string;
  phone: string | null;
  store_id: string | null;
  points: number;
  lifetime_points: number;
  /** Tier at the card's store, worked out by the server from that store's own tiers. */
  tier: string;
  /** The store's own cheapest reward these points don't reach yet; null when there is none (no meter then). */
  next_reward?: { name: string; cost: number } | null;
}

export interface WalletReceipt {
  id: string;
  order_number: string;
  total: number;
  points_earned: number;
  points_redeemed: number;
  paid_at: string;
  business_id: string;
  store_name: string;
  store_icon: string | null;
}

export interface Wallet {
  cards: WalletCard[];
  receipts: WalletReceipt[];
}

const EMPTY: Wallet = { cards: [], receipts: [] };

/** One read of the wallet: every card and receipt the tickets on this phone open. */
export const fetchWallet = (): Promise<Wallet> => {
  const held = tickets();
  const token = accountToken();
  return held.length === 0 && !token ? Promise.resolve(EMPTY) : api<Wallet>("/auth/guest-wallet", { tickets: held, accountToken: token });
};

// The card a guest just tapped in Cards. Opening it reloads into the store, where the member takes a
// moment to sync; this lets the card screen draw the code and points straight away.
const TAPPED_KEY = "cliqsuite_guest_tapped_card";
export function rememberTappedCard(card: WalletCard) {
  try {
    sessionStorage.setItem(TAPPED_KEY, JSON.stringify(card));
  } catch {
    // no storage: the card screen shows its placeholder instead
  }
}
export function tappedCard(businessId: string): WalletCard | null {
  try {
    const card = JSON.parse(sessionStorage.getItem(TAPPED_KEY) ?? "null") as WalletCard | null;
    return card?.business_id === businessId ? card : null;
  } catch {
    return null;
  }
}

/** Cards and receipts for every business this phone has joined. `null` while loading. */
export function useWallet(): { wallet: Wallet | null; failed: boolean } {
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    fetchWallet()
      .then(setWallet)
      .catch(() => setFailed(true));
  }, []);
  return { wallet, failed };
}
